/**
 * 注册流程 Hook（区域适配版）
 * 根据用户所在区域在不同触发点显示协议确认弹窗
 *
 * 功能：
 * - CN 区域在发送验证码时触发弹窗
 * - 非 CN 区域在点击注册时触发弹窗
 * - 支持动作续传机制
 * - 自动记录用户同意的协议版本和区域
 *
 * **Validates: Requirements 5.1, 5.2, 5.5**
 */

import { useState, useCallback, useRef } from "react";
import { useIsPIPL, useRegion } from "@/stores/regionStore";
import { api } from "@/lib/api";

export interface UseRegistrationFlowOptions {
  /** 协议是否已勾选 */
  agreementsChecked: boolean;
  /** 设置协议勾选状态 */
  setAgreementsChecked: (checked: boolean) => void;
  /** 协议列表（用于记录签署） */
  agreements?: Array<{ id: number; type: string; version: string }>;
  /** 是否在同意时自动记录到后端（需要用户已登录） */
  autoRecordConsent?: boolean;
  /** 同意记录成功回调 */
  onConsentRecorded?: () => void;
  /** 同意记录失败回调 */
  onConsentRecordError?: (error: Error) => void;
}

export interface UseRegistrationFlowReturn {
  /** 弹窗是否显示 */
  showBlockingModal: boolean;
  /** 设置弹窗显示状态 */
  setShowBlockingModal: (show: boolean) => void;
  /** 当前区域 */
  region: string;
  /** 是否为 CN 区域（PIPL 模式） */
  isPIPL: boolean;
  /** 是否正在记录同意 */
  isRecordingConsent: boolean;
  /**
   * 处理发送验证码操作
   * CN 区域：如果未勾选协议，显示弹窗
   * 非 CN 区域：直接执行
   */
  handleSendVerificationCode: (action: () => void | Promise<void>) => void;
  /**
   * 处理注册操作
   * CN 区域：直接执行（已在发送验证码时确认）
   * 非 CN 区域：如果未勾选协议，显示弹窗
   */
  handleRegister: (action: () => void | Promise<void>) => void;
  /**
   * 手动记录用户同意（用于已登录用户）
   */
  recordConsent: (agreementIds: number[]) => Promise<void>;
  /** 弹窗属性 */
  modalProps: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onAgree: (agreementIds?: number[]) => void;
    onDisagree: () => void;
    agreements?: Array<{ id: number; type: string; version: string }>;
  };
}

/**
 * 注册流程 Hook
 *
 * 根据区域在不同触发点显示协议确认弹窗：
 * - CN 区域（PIPL）：在发送验证码时触发
 * - 非 CN 区域：在点击注册时触发
 *
 * @example
 * ```tsx
 * const {
 *   handleSendVerificationCode,
 *   handleRegister,
 *   modalProps,
 * } = useRegistrationFlow({
 *   agreementsChecked,
 *   setAgreementsChecked,
 *   agreements,
 * });
 *
 * // CN 区域发送验证码
 * const sendCode = () => {
 *   handleSendVerificationCode(async () => {
 *     await api.sendVerificationCode(phone);
 *   });
 * };
 *
 * // 注册
 * const register = () => {
 *   handleRegister(async () => {
 *     await api.register(formData);
 *   });
 * };
 *
 * return (
 *   <>
 *     <Button onClick={sendCode}>获取验证码</Button>
 *     <Button onClick={register}>注册</Button>
 *     <RegistrationBlockingModal {...modalProps} />
 *   </>
 * );
 * ```
 */
export function useRegistrationFlow({
  agreementsChecked,
  setAgreementsChecked,
  agreements = [],
  autoRecordConsent = false,
  onConsentRecorded,
  onConsentRecordError,
}: UseRegistrationFlowOptions): UseRegistrationFlowReturn {
  const [showBlockingModal, setShowBlockingModal] = useState(false);
  const [isRecordingConsent, setIsRecordingConsent] = useState(false);
  const pendingActionRef = useRef<(() => void | Promise<void>) | null>(null);

  // 从 RegionStore 获取区域信息
  const region = useRegion();
  const isPIPL = useIsPIPL();

  /**
   * 记录用户同意到后端
   */
  const recordConsent = useCallback(
    async (agreementIds: number[]) => {
      if (agreementIds.length === 0) return;

      setIsRecordingConsent(true);
      try {
        await api.post("/api/v1/agreements/sign", {
          agreement_ids: agreementIds,
        });
        onConsentRecorded?.();
      } catch (error) {
        onConsentRecordError?.(error as Error);
        throw error;
      } finally {
        setIsRecordingConsent(false);
      }
    },
    [onConsentRecorded, onConsentRecordError]
  );

  /**
   * 检查协议并执行操作
   * 如果已勾选协议，直接执行操作
   * 如果未勾选协议，显示弹窗并存储待执行操作
   */
  const checkAndProceed = useCallback(
    (action: () => void | Promise<void>) => {
      if (agreementsChecked) {
        // 已勾选协议，直接执行操作
        action();
      } else {
        // 未勾选协议，存储操作并显示弹窗
        pendingActionRef.current = action;
        setShowBlockingModal(true);
      }
    },
    [agreementsChecked]
  );

  /**
   * 处理发送验证码操作
   * CN 区域：如果未勾选协议，显示弹窗
   * 非 CN 区域：直接执行
   */
  const handleSendVerificationCode = useCallback(
    (action: () => void | Promise<void>) => {
      if (isPIPL) {
        // CN 区域在发送验证码时触发协议确认
        checkAndProceed(action);
      } else {
        // 非 CN 区域直接发送验证码
        action();
      }
    },
    [isPIPL, checkAndProceed]
  );

  /**
   * 处理注册操作
   * CN 区域：直接执行（已在发送验证码时确认）
   * 非 CN 区域：如果未勾选协议，显示弹窗
   */
  const handleRegister = useCallback(
    (action: () => void | Promise<void>) => {
      if (isPIPL) {
        // CN 区域已在发送验证码时确认，直接注册
        // 但仍需检查协议是否已勾选（防止用户跳过验证码步骤）
        if (agreementsChecked) {
          action();
        } else {
          checkAndProceed(action);
        }
      } else {
        // 非 CN 区域在点击注册时触发协议确认
        checkAndProceed(action);
      }
    },
    [isPIPL, agreementsChecked, checkAndProceed]
  );

  /**
   * 用户同意回调
   * 自动勾选协议并执行待执行操作
   * 如果启用了 autoRecordConsent，还会记录到后端
   */
  const handleAgree = useCallback(
    async (agreementIds?: number[]) => {
      // 自动勾选协议
      setAgreementsChecked(true);

      // 如果启用了自动记录且有协议 ID，记录到后端
      if (autoRecordConsent && agreementIds && agreementIds.length > 0) {
        try {
          await recordConsent(agreementIds);
        } catch {
          // 记录失败不阻止后续操作，错误已通过回调处理
        }
      }

      // 执行待执行操作（动作续传）
      if (pendingActionRef.current) {
        const action = pendingActionRef.current;
        pendingActionRef.current = null;
        // 使用 setTimeout 确保状态更新后再执行操作
        setTimeout(() => {
          action();
        }, 0);
      }
    },
    [setAgreementsChecked, autoRecordConsent, recordConsent]
  );

  /**
   * 用户拒绝回调
   * 清除待执行操作
   */
  const handleDisagree = useCallback(() => {
    pendingActionRef.current = null;
  }, []);

  /**
   * 弹窗状态变化回调
   */
  const handleOpenChange = useCallback((open: boolean) => {
    setShowBlockingModal(open);
    if (!open) {
      // 弹窗关闭时清除待执行操作
      pendingActionRef.current = null;
    }
  }, []);

  return {
    showBlockingModal,
    setShowBlockingModal,
    region,
    isPIPL,
    isRecordingConsent,
    handleSendVerificationCode,
    handleRegister,
    recordConsent,
    modalProps: {
      open: showBlockingModal,
      onOpenChange: handleOpenChange,
      onAgree: handleAgree,
      onDisagree: handleDisagree,
      agreements,
    },
  };
}

export default useRegistrationFlow;
